import styled from "@emotion/styled";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import {
  useRecoilState,
  useRecoilValue,
  useResetRecoilState,
  useSetRecoilState,
} from "recoil";
import { ContactApi } from "../../api/Contacts";
import { getAllUserSavedSearches } from "../../api/SavedSearches";
import {
  ContactFilterAtom,
  IsDefaultStatusAtom,
} from "../../atoms/ContactFilterAtom";
import {
  ClearFilterAtom,
  ContactResetTagSelect,
  FilterCounterAtom,
  ShowSidebarFilter,
} from "../../atoms/ContactFilterAtoms";
import { ContactFiltersAtom } from "../../atoms/ContactFiltersAtom";
import { LabelAtom } from "../../atoms/LabelAtom";
import { CountedSavedSearchesAtom } from "../../atoms/SavedSearches";
import { TestDriveBannerAtom } from "../../atoms/TestDriveBannerAtom";
import { INTERNAL_UIDS_BETA } from "../../utils/constants";
import { localStorageGetItem } from "../../utils/LocalStorage";
import AccordionItem from "../AccordionItem";
import FilterByInteraction from "./Sidebar/FilterByInteraction";
import FilterByLabel from "./Sidebar/FilterByLabel";
import FilterBySavedSearch from "./Sidebar/FilterBySavedSearch";
import FilterByStatus, { allStatusArray } from "./Sidebar/FilterByStatus";
import FilterByTags, { NewRecipientTag } from "./Sidebar/FilterByTags";
import { Circle } from "./Sidebar/SidebarStyles";

const Container = styled(motion.div)<{
  showSideBar: boolean;
  hasTestDriverBanner: boolean;
}>`
  width: 400px;
  height: 100vh;

  position: fixed;
  right: 0;
  bottom: 0;
  z-index: 1001;

  background-color: white;
  border-left: 1px solid #eaeaea;
  opacity: ${(props) => (props.showSideBar ? 1 : 0)};
`;

export const CloseButtonWrapper = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;

  padding: 8px;

  cursor: pointer;

  svg {
    width: 24px;
    height: 24px;
    color: #868482;
  }
`;

export const FilterHeader = styled.div`
  width: 100%;

  padding: 16px 34px 16px 24px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  border-top: 1px solid #eaeaea;
  border-bottom: 1px solid #eaeaea;
`;

const FilterCounterDiv = styled.div`
  display: flex;
  align-items: center;
`;

const ItemTitle = styled.div`
  color: #3e3e3e;
  font-size: 16px;
`;

const ItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: scroll;
  height: calc(100% - 60px);
`;

const Overlay = styled(motion.div)<{ showSideBar: boolean }>`
  width: 100%;
  height: 100vh;

  display: block;

  background: rgba(0, 0, 0, 0.24);

  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  z-index: 1000;

  opacity: ${(props) => (props.showSideBar ? 1 : 0)};

  @media screen and (min-width: 1520px) {
    display: none;
  }
`;

enum FiltersEnum {
  label = "label",
  status = "status",
  tags = "tags",
  savedSearch = "savedSearch",
  interaction = "interaction",
}

interface Filters {
  label: boolean;
  status: boolean;
  tags: boolean;
  savedSearch: boolean;
  interaction: boolean;
}

export default function SidebarFilters() {
  const showTestDrive = useRecoilValue(TestDriveBannerAtom);

  const filterCounter = useRecoilValue(FilterCounterAtom);
  const resetFilterCounter = useResetRecoilState(FilterCounterAtom);
  const [contactFilter, setContactFilter] = useRecoilState(ContactFilterAtom);
  const setContactFilters = useSetRecoilState(ContactFiltersAtom);
  const resetContactFilters = useResetRecoilState(ContactFiltersAtom);
  const labels = useRecoilValue(LabelAtom);
  const setIsDefaultStatus = useSetRecoilState(IsDefaultStatusAtom);

  const [showSideBar, setShowSideBar] = useRecoilState(ShowSidebarFilter);
  const setCountedSavedSearches = useSetRecoilState(CountedSavedSearchesAtom);

  const [shouldClear, setShouldClear] = useRecoilState(ClearFilterAtom);
  const countedSavedSearches = useRecoilValue(CountedSavedSearchesAtom);
  const resetTagSelect = useSetRecoilState(ContactResetTagSelect);

  const [totalCount, setTotalCount] = useState(0);

  const [showInteraction, setShowInteraction] = useState(false);

  useEffect(() => {
    let internalUid = localStorageGetItem("INTERNAL_USER_UID") as string;

    if (INTERNAL_UIDS_BETA.includes(internalUid)) setShowInteraction(true);
  }, []);

  const savedSearchesSuggestions = countedSavedSearches.map((savedSearch) => ({
    value: savedSearch.id,
    name: savedSearch.name,
    icon: "tag",
    suffix: `${savedSearch.contactsCount}`,
  }));

  const [openedFilters, setOpenedFilters] = useState<Filters>({
    label: false,
    savedSearch: false,
    status: false,
    tags: false,
    interaction: false,
  });

  async function getSavedSearches() {
    const savedSearchesResponse = await getAllUserSavedSearches();

    if (savedSearchesResponse) {
      const tagQueries = savedSearchesResponse.map(({ search }) => search);
      const counts = await ContactApi.countContactsByTagQueries(tagQueries);

      const countedSavedSearchesResponse = savedSearchesResponse.map(
        (savedSearch, index) => {
          return {
            ...savedSearch,
            contactsCount: counts[index],
          };
        }
      );

      setCountedSavedSearches(countedSavedSearchesResponse);
    }
  }

  // Update saved searches
  useEffect(() => {
    getSavedSearches();
  }, [showSideBar]);

  useEffect(() => {
    const total = Object.values(filterCounter).reduce(
      (previousValue, currentValue) => previousValue + currentValue
    );

    setTotalCount(total);
  }, [filterCounter]);

  const recipientTags = contactFilter?.recipientTags
    ? contactFilter.recipientTags
    : [NewRecipientTag()];

  function handleToggleAccordion(id: FiltersEnum) {
    setOpenedFilters((openedFilters) => ({
      ...openedFilters,
      [id]: !openedFilters[id],
    }));
  }

  function clearAll() {
    resetFilterCounter();
    resetContactFilters();
    setIsDefaultStatus(true);
    setShouldClear(true);
    resetTagSelect((t) => !t);
    setContactFilter(null);
  }

  useEffect(() => {
    if (shouldClear)
      setTimeout(() => {
        setShouldClear(false);
      }, 100);
  }, [shouldClear]);

  const variants = {
    open: {
      opacity: 1,
      display: "block",
    },
    closed: {
      opacity: 0,
      x: 400,
      transitionEnd: {
        display: "none",
      },
    },
  };

  const variantsOverlay = {
    open: {
      opacity: 1,
      display: "block",
    },
    closed: {
      opacity: 0,
      transitionEnd: {
        display: "none",
      },
    },
  };

  return (
    <>
      <Overlay
        showSideBar={showSideBar}
        animate={showSideBar ? "open" : "closed"}
        variants={variantsOverlay}
        transition={{
          bounce: false,
        }}
        onClick={() => {
          setShowSideBar(false);
        }}
      />

      <Container
        onMouseLeave={() => setShowSideBar(false)}
        showSideBar={showSideBar}
        hasTestDriverBanner={showTestDrive.show}
        animate={showSideBar ? "open" : "closed"}
        variants={variants}
        transition={{
          bounce: false,
        }}
      >
        <FilterHeader>
          <FilterCounterDiv
            style={
              !!totalCount ? {} : { width: "100%", justifyContent: "center" }
            }
          >
            {<Circle isVisible={!!totalCount}>{totalCount}</Circle>}
            <ItemTitle
              style={
                !!totalCount
                  ? { marginLeft: "5px", fontWeight: 600 }
                  : { color: "#888888" }
              }
            >
              {!!totalCount
                ? `Filter${totalCount > 1 ? "s" : ""} selected`
                : `Enter your search criteria below`}
            </ItemTitle>
          </FilterCounterDiv>
          {!!totalCount && (
            <div
              style={{
                color: "#fb972e",
                fontWeight: 600,
                cursor: "pointer",
              }}
              onClick={clearAll}
            >
              Clear All
            </div>
          )}
        </FilterHeader>
        <ItemsContainer>
          <AccordionItem
            title={
              <FilterCounterDiv>
                <Circle isVisible={filterCounter.byLabelCount > 0}>
                  {filterCounter.byLabelCount}
                </Circle>
                <div style={{ marginLeft: "5px" }}>By Label</div>
              </FilterCounterDiv>
            }
            handleToggle={handleToggleAccordion}
            id={FiltersEnum.label}
            isOpen={openedFilters.label}
          >
            <FilterByLabel />
          </AccordionItem>
          <AccordionItem
            title={
              <FilterCounterDiv>
                <Circle isVisible={filterCounter.byStatusCount > 0}>
                  {filterCounter.byStatusCount}
                </Circle>
                <div style={{ marginLeft: "5px" }}>By Status</div>
              </FilterCounterDiv>
            }
            handleToggle={handleToggleAccordion}
            id={FiltersEnum.status}
            isOpen={openedFilters.status}
          >
            <FilterByStatus />
          </AccordionItem>
          {!contactFilter?.savedSearchName && (
            <AccordionItem
              title={
                <FilterCounterDiv>
                  <Circle isVisible={filterCounter.byTagsCount > 0}>
                    {filterCounter.byTagsCount}
                  </Circle>
                  <div style={{ marginLeft: "5px" }}>By Tags</div>
                </FilterCounterDiv>
              }
              handleToggle={handleToggleAccordion}
              id={FiltersEnum.tags}
              isOpen={openedFilters.tags}
            >
              <FilterByTags />
            </AccordionItem>
          )}
          {!(recipientTags[0]?.tags.length > 0) &&
            savedSearchesSuggestions.length > 0 && (
              <AccordionItem
                title={
                  <FilterCounterDiv>
                    <Circle isVisible={filterCounter.bySavedSearchCount > 0}>
                      {filterCounter.bySavedSearchCount}
                    </Circle>
                    <div style={{ marginLeft: "5px" }}>By Saved Search</div>
                  </FilterCounterDiv>
                }
                handleToggle={handleToggleAccordion}
                id={FiltersEnum.savedSearch}
                isOpen={openedFilters.savedSearch}
              >
                <FilterBySavedSearch />
              </AccordionItem>
            )}
          <AccordionItem
            title={
              <FilterCounterDiv>
                <Circle isVisible={filterCounter.byInteractionCount > 0}>
                  {filterCounter.byInteractionCount}
                </Circle>
                <div style={{ marginLeft: "5px" }}>By Interaction</div>
              </FilterCounterDiv>
            }
            handleToggle={handleToggleAccordion}
            id={FiltersEnum.interaction}
            isOpen={openedFilters.interaction}
          >
            <FilterByInteraction />
          </AccordionItem>
        </ItemsContainer>
      </Container>
    </>
  );
}
